import * as React from 'react';
import { Check } from 'lucide-react';
import { DMButton, DMDiv } from './Primitives';

export type ManagementTableRowId = string | number;

export type ManagementTableColumn<TRow> = {
  id: string;
  header: React.ReactNode;
  width: string;
  headerClassName?: string;
  cellClassName?: string;
  render: (row: TRow) => React.ReactNode;
};

type ManagementTableProps<TRow> = {
  rows: TRow[];
  columns: ManagementTableColumn<TRow>[];
  rowKey: (row: TRow) => ManagementTableRowId;
  selectedIds?: ManagementTableRowId[];
  onSelectAll?: (ids: ManagementTableRowId[], allSelected: boolean) => void;
  onSelectRow?: (id: ManagementTableRowId, row: TRow) => void;
  isRowSelectable?: (row: TRow) => boolean;
  getSelectLabel?: (row: TRow) => string;
  onRowClick?: (row: TRow) => void;
  emptyMessage?: string;
  isLoading?: boolean;
  skeletonRowCount?: number;
  renderSkeletonRow?: (index: number, columnCount: number) => React.ReactNode;
  dense?: boolean;
  selectColumnWidth?: string;
  className?: string;
  rowClassName?: string;
};

export function ManagementTable<TRow>({
  rows,
  columns,
  rowKey,
  selectedIds = [],
  onSelectAll,
  onSelectRow,
  isRowSelectable,
  getSelectLabel,
  onRowClick,
  emptyMessage = 'No records found',
  isLoading = false,
  skeletonRowCount = 8,
  renderSkeletonRow,
  dense = false,
  selectColumnWidth = '4%',
  className = '',
  rowClassName = '',
}: ManagementTableProps<TRow>) {
  const isSelectable = !!onSelectAll && !!onSelectRow;
  const selectableRows = isRowSelectable ? rows.filter(isRowSelectable) : rows;
  const rowIds = selectableRows.map(rowKey);
  const areRowsSelected =
    rowIds.length > 0 && rowIds.every(id => selectedIds.includes(id));
  const columnCount = columns.length + (isSelectable ? 1 : 0);

  return (
    <DMDiv
      className={`min-h-0 flex-1 overflow-auto rounded-[6px] border border-gray-300 bg-white ${className}`}
    >
      <DMDiv
        as="table"
        className="w-full table-fixed border-collapse text-left"
      >
        <DMDiv as="thead" className="sticky top-0 z-10 bg-white">
          <DMDiv
            as="tr"
            className={`${
              dense ? 'h-[5.1vh]' : 'h-[5.2vh]'
            } border-b border-gray-300 text-[1.5vh] font-semibold text-gray-900`}
          >
            {isSelectable && (
              <DMDiv
                as="th"
                className="border-r border-gray-300 px-[1.1vw]"
                style={{ width: selectColumnWidth }}
              >
                <DMButton
                  type="button"
                  onClick={() => onSelectAll(rowIds, areRowsSelected)}
                  className={`flex h-[2.1vh] w-[2.1vh] items-center justify-center rounded-[4px] border ${
                    areRowsSelected
                      ? 'border-[#173c43] bg-[#173c43] text-white'
                      : 'border-gray-300 bg-white text-transparent'
                  }`}
                >
                  <Check className="h-[1.5vh] w-[1.5vh]" strokeWidth={3} />
                </DMButton>
              </DMDiv>
            )}
            {columns.map(column => (
              <DMDiv
                as="th"
                key={column.id}
                className={`whitespace-nowrap break-normal border-r border-gray-300 px-[1.4vw] leading-[1.25] last:border-r-0 ${
                  column.headerClassName || ''
                }`}
                style={{ width: column.width }}
              >
                {column.header}
              </DMDiv>
            ))}
          </DMDiv>
        </DMDiv>
        <DMDiv as="tbody">
          {isLoading &&
            Array.from({ length: skeletonRowCount }).map((_, index) =>
              renderSkeletonRow ? (
                renderSkeletonRow(index, columnCount)
              ) : (
                <DMDiv
                  as="tr"
                  key={`management-table-skeleton-${index}`}
                  className="h-[5.1vh] border-b border-gray-300"
                >
                  {Array.from({ length: columnCount }).map((__, cellIndex) => (
                    <DMDiv
                      as="td"
                      key={cellIndex}
                      className="border-r border-gray-300 px-[1.4vw] last:border-r-0"
                    >
                      <DMDiv className="h-[1.8vh] w-[75%] animate-pulse rounded bg-gray-200" />
                    </DMDiv>
                  ))}
                </DMDiv>
              )
            )}
          {!isLoading && !rows.length && (
            <DMDiv as="tr">
              <DMDiv
                as="td"
                {...({
                  colSpan: columnCount,
                } as React.TdHTMLAttributes<HTMLTableCellElement>)}
                className="h-[34vh] text-center text-[1.8vh] font-medium text-gray-500"
              >
                {emptyMessage}
              </DMDiv>
            </DMDiv>
          )}
          {!isLoading &&
            rows.map(row => {
              const id = rowKey(row);
              const isSelected = selectedIds.includes(id);
              const rowSelectable = isRowSelectable
                ? isRowSelectable(row)
                : true;

              return (
                <DMDiv
                  as="tr"
                  key={id}
                  onClick={() => onRowClick?.(row)}
                  className={`${
                    dense ? 'h-[5.1vh]' : 'h-[5vh]'
                  } border-b border-gray-300 text-[1.5vh] font-medium text-gray-600 transition-colors hover:bg-gray-50 ${
                    onRowClick ? 'cursor-pointer' : ''
                  } ${rowClassName}`}
                >
                  {isSelectable && (
                    <DMDiv
                      as="td"
                      className="border-r border-gray-300 px-[1.1vw]"
                    >
                      <DMButton
                        type="button"
                        onClick={event => {
                          event.stopPropagation();
                          if (!rowSelectable) {
                            return;
                          }

                          onSelectRow(id, row);
                        }}
                        disabled={!rowSelectable}
                        className={`flex h-[2vh] w-[2vh] items-center justify-center rounded-[4px] border disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-transparent ${
                          isSelected
                            ? 'border-[#173c43] bg-[#173c43] text-white'
                            : 'border-gray-300 bg-white text-transparent'
                        }`}
                        aria-label={getSelectLabel?.(row)}
                      >
                        <Check
                          className="h-[1.4vh] w-[1.4vh]"
                          strokeWidth={3}
                        />
                      </DMButton>
                    </DMDiv>
                  )}
                  {columns.map(column => (
                    <DMDiv
                      as="td"
                      key={column.id}
                      className={`whitespace-normal break-words border-r border-gray-300 px-[1.4vw] py-[0.8vh] leading-[1.35] [overflow-wrap:anywhere] last:border-r-0 ${
                        column.cellClassName || ''
                      }`}
                    >
                      {column.render(row)}
                    </DMDiv>
                  ))}
                </DMDiv>
              );
            })}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
}
